import cls from 'classnames';
import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import newsletterImageFallback from '@/assets/form-images/form-image.svg';
import CrossIcon from '@/components/icons/icon-cross';
import Image from '@/components/image';
import Portal from '@/components/portal';
import useLockBodyScroll from '@/lib/hooks/use-lock-body-scroll';
import { OnboardingNewsletterTexts } from '@/lib/onboarding/onboarding-newsletter-texts';
import { selectUserProfile } from '@/store/slices/user';
import { ImageAsset } from '@/types/views/generic';

export type OnboardingSubscribeResult =
  | 'success'
  | 'rate_limited'
  | 'session_expired'
  | 'error';

type OnboardingNewsletterModalProps = {
  texts: OnboardingNewsletterTexts;
  image: ImageAsset | null;
  email: string;
  firstName: string;
  lastName: string;
  onDismissPermanent: () => Promise<boolean>;
  onDismissSoft: () => Promise<boolean>;
  onSubscribe: () => Promise<OnboardingSubscribeResult>;
};

const checkboxClassName = cls(
  'mt-0.5 w-5 h-5 shrink-0 rounded-[0.25rem] bg-transparent border border-white cursor-pointer',
  'text-red-medium accent-red-medium',
  'focus:outline-none focus:ring-1 focus:ring-gray-2 focus:ring-offset-0',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'checked:bg-red-medium checked:border-red-medium',
  'checked:focus:bg-red-medium checked:focus:border-red-medium',
);

const inputClassName = cls(
  'w-full rounded-2 py-3 px-4 leading-snug text-white font-semibold',
  'bg-black border border-transparent',
  'outline-none focus:outline-none focus:ring-0 focus:border-transparent',
  'placeholder-gray-2',
  'read-only:cursor-default read-only:opacity-80',
);

const OnboardingNewsletterModal: FC<OnboardingNewsletterModalProps> = ({
  texts,
  image,
  email,
  firstName,
  lastName,
  onDismissPermanent,
  onDismissSoft,
  onSubscribe,
}) => {
  useLockBodyScroll();

  const profile = useSelector(selectUserProfile);
  const userSub = profile?.sub;
  const [consentChecked, setConsentChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    window.dataLayer?.push({
      event: 'onboarding_newsletter_open',
      user_sub: userSub ?? null,
    });
    // Modal unmounts when closed; fire once per open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSubmit = consentChecked && !isSubmitting && !isDismissing;

  const consentDescriptionParts = useMemo(() => {
    const marker = '{privacyLink}';
    const [before, after] = texts.consentDescription.split(marker);

    return {
      before: before ?? texts.consentDescription,
      after: after ?? '',
      hasLink: texts.consentDescription.includes(marker),
    };
  }, [texts.consentDescription]);

  const dismiss = async (mode: 'permanent' | 'soft') => {
    window.dataLayer?.push({
      event:
        mode === 'permanent'
          ? 'onboarding_newsletter_dismiss'
          : 'onboarding_newsletter_soft_dismiss',
      user_sub: userSub ?? null,
    });

    if (isSubmitting || isDismissing) {
      return;
    }

    setIsDismissing(true);
    setErrorMessage(null);

    const persisted =
      mode === 'permanent' ? await onDismissPermanent() : await onDismissSoft();

    if (persisted) {
      window.dataLayer?.push({
        event:
          mode === 'permanent'
            ? 'onboarding_newsletter_dismiss_success'
            : 'onboarding_newsletter_soft_dismiss_success',
        user_sub: userSub ?? null,
      });

      return;
    }

    window.dataLayer?.push({
      event:
        mode === 'permanent'
          ? 'onboarding_newsletter_dismiss_error'
          : 'onboarding_newsletter_soft_dismiss_error',
      user_sub: userSub ?? null,
      error_code: 'error',
    });
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    window.dataLayer?.push({
      event: 'onboarding_newsletter_subscribe_click',
      user_sub: userSub ?? null,
    });

    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);

    const result = await onSubscribe();

    if (result === 'success') {
      window.dataLayer?.push({
        event: 'onboarding_newsletter_subscribe_success',
        user_sub: userSub ?? null,
      });

      return;
    }

    window.dataLayer?.push({
      event: 'onboarding_newsletter_subscribe_error',
      user_sub: userSub ?? null,
      error_code: result,
    });

    if (result === 'rate_limited') {
      setErrorMessage(texts.rateLimitMessage);
    } else if (result === 'session_expired') {
      setErrorMessage(texts.sessionExpiredMessage);
    } else {
      setErrorMessage(texts.errorMessage);
    }

    setIsSubmitting(false);
  };

  const renderCloseButton = (className: string, fill?: string) => (
    <button
      type="button"
      aria-label="Schliessen"
      className={className}
      onClick={() => dismiss('permanent')}
      disabled={isSubmitting || isDismissing}
    >
      <CrossIcon className="h-4 w-4 md:h-5 md:w-5" fill={fill} />
    </button>
  );

  const renderActionButtons = (wrapperClassName: string) => (
    <div className={cls('flex flex-col items-center', wrapperClassName)}>
      <button
        type="submit"
        disabled={!canSubmit}
        className={cls(
          'w-full rounded-2 py-3 px-6 text-body1 font-semibold text-white',
          'bg-red-medium hover:bg-red',
          'disabled:bg-gray-2 disabled:cursor-not-allowed',
        )}
      >
        {texts.submitButton}
      </button>

      <button
        type="button"
        onClick={() => dismiss('soft')}
        disabled={isSubmitting || isDismissing}
        className="mt-3 text-body2 text-white underline disabled:opacity-50"
      >
        {texts.dismissButton}
      </button>
    </div>
  );

  return (
    <Portal>
      <div className="fixed inset-0 z-90 flex flex-col bg-black/70 md:items-center md:justify-center md:px-4 md:py-8">
        <div
          className={cls(
            'relative flex h-[100vh] h-[100dvh] w-full flex-col overflow-hidden',
            'bg-[rgb(43,42,45)] text-white shadow-lg',
            'md:h-auto md:max-h-[85vh] md:max-h-[85dvh] md:max-w-[920px] md:rounded-4',
          )}
          role="dialog"
          aria-modal="true"
          aria-labelledby="onboarding-newsletter-title"
        >
          <div
            className={cls(
              'sticky top-0 z-10 flex shrink-0 items-center justify-end',
              'bg-[rgb(43,42,45)] px-4 pb-2',
              'pt-[max(0.75rem,env(safe-area-inset-top))] md:hidden',
            )}
          >
            {renderCloseButton(
              cls(
                'flex h-8 w-8 items-center justify-center rounded-16 bg-white',
                'text-black hover:opacity-80 disabled:opacity-50',
              ),
            )}
          </div>

          {renderCloseButton(
            cls(
              'absolute right-4 top-4 z-10 hidden h-8 w-8 items-center justify-center',
              'text-white hover:opacity-80 disabled:opacity-50 md:flex',
            ),
            '#ffffff',
          )}

          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="grid gap-6 p-6 md:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] md:gap-10 md:p-10 md:pt-10">
                <div className="flex h-full items-center justify-center">
                  <figure className="relative h-[170px] w-full max-w-[360px] md:h-[280px]">
                    <Image
                      src={image?.src || newsletterImageFallback.src}
                      alt={image?.description || ''}
                      objectFit="contain"
                      objectPosition="center"
                      className="h-full w-full"
                    />
                  </figure>
                </div>

                <div className="flex flex-col">
                  <h2
                    id="onboarding-newsletter-title"
                    className="mb-3 text-[1.75rem] font-bold leading-tight md:text-[2.25rem]"
                  >
                    <span className="text-white">{texts.titlePrefix} </span>
                    <span className="text-white">{texts.titleBrand}</span>
                  </h2>

                  <p className="mb-6 text-body2 text-white md:mb-8">
                    {texts.description}
                  </p>

                  <div className="flex flex-col gap-4">
                    <label className="flex flex-col gap-2">
                      <span className="text-overline text-gray-3">
                        {texts.emailLabel}
                      </span>
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        value={email}
                        readOnly
                        className={inputClassName}
                        disabled={isSubmitting || isDismissing}
                      />
                    </label>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <label className="flex flex-col gap-2">
                        <span className="text-overline text-gray-3">
                          {texts.firstNameLabel}
                        </span>
                        <input
                          type="text"
                          name="firstName"
                          autoComplete="given-name"
                          value={firstName}
                          readOnly
                          className={inputClassName}
                          disabled={isSubmitting || isDismissing}
                        />
                      </label>

                      <label className="flex flex-col gap-2">
                        <span className="text-overline text-gray-3">
                          {texts.lastNameLabel}
                        </span>
                        <input
                          type="text"
                          name="lastName"
                          autoComplete="family-name"
                          value={lastName}
                          readOnly
                          className={inputClassName}
                          disabled={isSubmitting || isDismissing}
                        />
                      </label>
                    </div>

                    <div className="mt-1 flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="onboarding-newsletter-consent"
                        checked={consentChecked}
                        onChange={(event) => {
                          const { checked } = event.target;
                          setConsentChecked(checked);

                          if (checked) {
                            window.dataLayer?.push({
                              event: 'onboarding_newsletter_consent_check',
                              user_sub: userSub ?? null,
                            });
                          }
                        }}
                        className={checkboxClassName}
                        disabled={isSubmitting || isDismissing}
                      />
                      <div className="flex flex-col">
                        <label
                          htmlFor="onboarding-newsletter-consent"
                          className="cursor-pointer select-none text-body1 font-semibold text-white"
                        >
                          {texts.consentLabel}
                        </label>
                        <p className="mt-1 text-body2 text-white">
                          {consentDescriptionParts.before}
                          {consentDescriptionParts.hasLink ? (
                            <a
                              href={texts.privacyLinkHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-medium underline"
                            >
                              {texts.privacyLinkLabel}
                            </a>
                          ) : null}
                          {consentDescriptionParts.after}
                        </p>
                      </div>
                    </div>

                    {errorMessage && (
                      <p className="text-small text-red-medium" role="alert">
                        {errorMessage}
                      </p>
                    )}

                    {renderActionButtons('mt-2 hidden flex-col md:flex')}
                  </div>
                </div>
              </div>
            </div>

            {renderActionButtons(
              cls(
                'sticky bottom-0 shrink-0 border-t border-white/10',
                'bg-[rgb(43,42,45)] px-6 pt-3',
                'pb-[max(1rem,env(safe-area-inset-bottom))] md:hidden',
              ),
            )}
          </form>
        </div>
      </div>
    </Portal>
  );
};

export default OnboardingNewsletterModal;
